import { ErrorSidePanel } from '@/components/errors';
import { useGetDevices } from '@/hooks/api';
import {
  ModuleName,
  Screen,
  type SkinPageConfig,
  templates,
  utils,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { useDeleteDevice } from '../../../-hooks/delete-device.hooks';
import { useDeleteAllDevices } from './-hooks/remove-all-devices.hook';
import { type DeviceManagementProp } from './-model/props';
import { DeviceManagementLight, DeviceManagementWooDark, DeviceManagementWoo } from './-skins';
import z from 'zod';

export const Route = createFileRoute('/(tv)/tv/$id/(device-management)/_sheet/device-management/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  validateSearch: z
    .object({
      device_name: z.string().nullish(),
      device_type: z.string().nullish(),
      redirectOnDelete: z.preprocess((v) => v === true || v === 'true', z.boolean()),
      redirect_uri: z.string().nullish(),
    })
    .nullish(),
  staticData: {
    module: ModuleName.DeviceManagement,
    screen: Screen.LandingPage,
  },
});

function RouteComponent() {
  const search = Route.useSearch();
  const { id } = Route.useParams();
  const { data: devices, isLoadingData } = useGetDevices(id);
  const { mutateAsync: deleteDevice, isPending: deleteDeviceLoading } = useDeleteDevice();
  const { mutateAsync: deleteAllDevices, isPending: deleteAllDevicesLoading } =
    useDeleteAllDevices();

  const props: DeviceManagementProp = {
    devices: devices ?? [],
    isLoading: isLoadingData,
    removeDevice: async (device) => {
      if (device.IsLocked || deleteDeviceLoading) return;
      await deleteDevice({ id: device.Id, sa: id });
      if (search?.redirectOnDelete && search?.redirect_uri) {
        utils.redirect(search.redirect_uri);
      }
    },
    removeAllDevices: async () => {
      if (deleteAllDevicesLoading) return;
      await deleteAllDevices({
        devices: devices?.filter((d) => !d.IsLocked).map((device) => device.Id) ?? [],
        sa: id,
      });
    },
    removeLoading: deleteDeviceLoading || deleteAllDevicesLoading,
    canDeleteAllDevices:
      isLoadingData || (devices?.reduce((acc, device) => acc + +!device.IsLocked, 0) ?? 0) >= 2,
  };

  return templates.getSkinSelector(getSkinConfig(props));
}

function getSkinConfig(props: DeviceManagementProp): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <DeviceManagementWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <DeviceManagementWoo {...props} />,
      },
    ],
    defaultValue: <DeviceManagementLight {...props} />,
  };
}
